import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { api } from '../../services/api';
import { formatSubjectName } from '../../utils/formatSubjectName';
import { TzBinding, TzTemplate } from './tzResolve';
import './TzTemplatesModal.css';

type Subject = { id: number | string; name: string | null | undefined };

type Feedback = {
  score_avg: number | null;
  score_count: number;
  mine: { subject_id: number | null; score: number }[];
  comments: { id: string; body: string; author: string; created_at: string; mine: boolean }[];
};

type Draft = {
  id: string | null;
  name: string;
  prompt: string;
  model_id: string;
  fill_desc: boolean;
  fill_eng_desc: boolean;
  fill_season: boolean;
  is_public: boolean;
  is_owner: boolean;
  browser_on: boolean;
  browser_model_id: string;
  browser_prompt: string;
  model_search: boolean;
};

const TOKENS = [
  { key: 'url', label: 'Ссылка', hint: 'адрес карточки' },
  { key: 'name', label: 'Название', hint: 'из нашей базы' },
  { key: 'niche', label: 'Ниша', hint: 'предмет' },
  { key: 'article', label: 'Артикул', hint: 'наш артикул' },
] as const;

const TOKEN_LABEL: Record<string, string> = {
  ...Object.fromEntries(TOKENS.map((token) => [token.key, token.label])),
  сбор: 'Сбор',
};

const MODEL_LABELS: Record<string, string> = {
  'deepseek-r1': 'DeepSeek R1',
  'deepseek-v4-flash': 'DeepSeek V4 Flash',
  'deepseek-v4-pro': 'DeepSeek V4 Pro',
  'glm-5.2': 'GLM 5.2',
  'glm-5.3': 'GLM 5.3',
  'gpt-5.6-luna': 'GPT-5.6 Luna',
  'gpt-6-luna': 'GPT-6 Luna',
  'kimi-k2.6': 'Kimi K2.6',
  'kimi-k3': 'Kimi K3',
  'qwen-3.7-max': 'Qwen 3.7 Max',
  'qwen3.8-max': 'Qwen 3.8 Max',
};

function modelLabel(id: string) {
  return MODEL_LABELS[id] || id;
}

// Каталог Beget отдаёт только цену. Зрение — по карточкам моделей; новый id без метки, пока его не добавят сюда.
const MODEL_VISION = new Set(['gpt-5.6-luna', 'gpt-6-luna', 'kimi-k3', 'qwen3.8-max']);

function modelSees(id: string) {
  return MODEL_VISION.has(id);
}

function ModelName({ id, missing, vision }: { id: string; missing?: boolean; vision?: boolean }) {
  return (
    <span className="tz-model-name">
      <span>{modelLabel(id)}{missing ? ' · нет в каталоге' : ''}</span>
      {(vision || modelSees(id)) && <i className="tz-vision" title="Видит фото">зрение</i>}
    </span>
  );
}

function money(value: number | null | undefined) {
  if (value == null || !Number.isFinite(value)) return '—';
  const digits = Math.abs(value) >= 100 ? 0 : 2;
  return `${new Intl.NumberFormat('ru-RU', { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(value)} ₽`;
}

function tokenCount(value: number | null | undefined) {
  if (value == null || !Number.isFinite(value)) return '—';
  return `${new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(Math.round(value))} ток.`;
}

function runsWord(count: number) {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return 'генерация';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'генерации';
  return 'генераций';
}

type SpendRow = {
  key: string;
  label: string;
  runs: number;
  prompt_tokens: number;
  completion_tokens: number;
  tokens: number;
  cost_rub: number | null;
  avg_cost: number | null;
  avg_tokens: number | null;
};

type SpendReport = {
  totals: SpendRow;
  by_model: SpendRow[];
  by_niche: SpendRow[];
  by_source: SpendRow[];
  by_template: SpendRow[];
};

const SPEND_SLICES = [
  { id: 'template', title: 'Шаблоны', rows: 'by_template' },
  { id: 'model', title: 'Модели', rows: 'by_model' },
  { id: 'niche', title: 'Ниши', rows: 'by_niche' },
  { id: 'source', title: 'Площадки', rows: 'by_source' },
] as const;

type TzModel = { id: string; input: number | null; output: number | null; missing?: boolean; vision?: boolean };

function asModels(raw: unknown): TzModel[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item): TzModel[] => {
    if (typeof item === 'string') return item ? [{ id: item, input: null, output: null }] : [];
    const row = item as TzModel;
    if (!row?.id) return [];
    return [{
      id: String(row.id),
      input: row.input == null || Number.isNaN(Number(row.input)) ? null : Number(row.input),
      output: row.output == null || Number.isNaN(Number(row.output)) ? null : Number(row.output),
      vision: row.vision === true,
    }];
  }).filter((row) => !/embedding/i.test(row.id));
}

function scopeOf(isPublic: boolean, subject: string, source: string): TzBinding['scope'] {
  if (subject) return isPublic ? 'niche' : 'user_niche';
  if (source) return isPublic ? 'source' : 'user_source';
  return isPublic ? 'global' : 'user';
}

function rub(value: number | null) {
  if (value == null || !Number.isFinite(value)) return '—';
  const fraction = Number.isInteger(value) ? 0 : value >= 10 ? 1 : 2;
  return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: fraction, minimumFractionDigits: 0 }).format(value);
}

function useCloseOutside(open: boolean, close: () => void) {
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event: MouseEvent) => {
      if (box.current?.contains(event.target as Node)) return;
      close();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, close]);
  return box;
}

function when(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('ru-RU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

const emptyDraft = (): Draft => ({
  id: null,
  name: '',
  prompt: '',
  model_id: '',
  fill_desc: true,
  fill_eng_desc: false,
  fill_season: false,
  is_public: false,
  is_owner: true,
  browser_on: false,
  browser_model_id: '',
  browser_prompt: '',
  model_search: false,
});

function draftFrom(item: TzTemplate): Draft {
  return {
    id: item.id,
    name: item.name,
    prompt: item.prompt,
    model_id: item.model_id,
    fill_desc: item.fill_desc,
    fill_eng_desc: item.fill_eng_desc,
    fill_season: item.fill_season,
    is_public: item.is_public,
    is_owner: item.is_owner !== false,
    browser_on: Boolean(item.browser_model_id),
    browser_model_id: item.browser_model_id || '',
    browser_prompt: item.browser_prompt || '',
    model_search: item.model_search === true,
  };
}

function makeChip(key: string, label: string) {
  const chip = document.createElement('span');
  chip.className = 'tz-token';
  chip.contentEditable = 'false';
  chip.dataset.token = key;
  chip.draggable = true;
  chip.textContent = label;
  chip.addEventListener('dragstart', (event) => {
    event.stopPropagation();
    event.dataTransfer?.setData('text/tz-token', key);
    event.dataTransfer?.setData('text/plain', key);
    chip.dataset.dragging = '1';
    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'copy';
  });
  chip.addEventListener('dragend', () => {
    delete chip.dataset.dragging;
  });
  return chip;
}

function appendText(root: HTMLElement, text: string) {
  const lines = text.split('\n');
  lines.forEach((line, index) => {
    if (index > 0) root.appendChild(document.createElement('br'));
    if (line) root.appendChild(document.createTextNode(line));
  });
}

function paint(root: HTMLElement, value: string, labels: Record<string, string>) {
  root.replaceChildren();
  const pattern = /\{\{\s*([^{}]+?)\s*\}\}/g;
  let last = 0;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(value))) {
    const raw = match[1].trim();
    const key = raw.toLowerCase();
    const label = TOKEN_LABEL[raw] || TOKEN_LABEL[key] || labels[key];
    appendText(root, value.slice(last, match.index));
    if (label) root.appendChild(makeChip(TOKEN_LABEL[raw] ? raw : key, label));
    else appendText(root, match[0]);
    last = match.index + match[0].length;
  }
  appendText(root, value.slice(last));
}

function serialize(root: HTMLElement) {
  const parts: string[] = [];
  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      parts.push((node.textContent || '').replace(/\u00a0/g, ' '));
      return;
    }
    if (!(node instanceof HTMLElement)) return;
    if (node.dataset.token) {
      parts.push(`{{${node.dataset.token}}}`);
      return;
    }
    if (node.tagName === 'BR') {
      parts.push('\n');
      return;
    }
    const block = node.tagName === 'DIV' || node.tagName === 'P';
    if (block && parts.length && !parts[parts.length - 1].endsWith('\n')) parts.push('\n');
    node.childNodes.forEach(walk);
  };
  root.childNodes.forEach(walk);
  return parts.join('');
}

function rangeFromPoint(x: number, y: number) {
  const doc = document as Document & {
    caretRangeFromPoint?: (x: number, y: number) => Range | null;
    caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
  };
  if (doc.caretRangeFromPoint) return doc.caretRangeFromPoint(x, y);
  const pos = doc.caretPositionFromPoint?.(x, y);
  if (!pos) return null;
  const range = document.createRange();
  range.setStart(pos.offsetNode, pos.offset);
  range.collapse(true);
  return range;
}

type PromptBlock = { id: string; name: string; key: string; body: string };

function blockKey(name: string) {
  return name.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^\p{L}\p{N}_-]/gu, '').slice(0, 40);
}

function PromptEditor({
  value,
  disabled,
  blocks,
  gather,
  onChange,
  onSaveBlock,
  onDeleteBlock,
}: {
  value: string;
  disabled: boolean;
  blocks: PromptBlock[];
  gather: boolean;
  onChange: (next: string) => void;
  onSaveBlock: (block: { id?: string; name: string; body: string }) => Promise<void>;
  onDeleteBlock: (id: string) => Promise<void>;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const caret = useRef<Range | null>(null);
  const [form, setForm] = useState<PromptBlock | null>(null);
  const [formError, setFormError] = useState('');
  const [formBusy, setFormBusy] = useState(false);
  const labels = useMemo(() => {
    const map: Record<string, string> = {};
    blocks.forEach((block) => {
      map[block.key] = block.name;
    });
    return map;
  }, [blocks]);
  const labelOf = (key: string) => TOKEN_LABEL[key] || labels[key.toLowerCase()] || key;

  useEffect(() => {
    const remember = () => {
      const root = ref.current;
      const sel = window.getSelection();
      if (!root || !sel?.rangeCount || !root.contains(sel.anchorNode)) return;
      caret.current = sel.getRangeAt(0).cloneRange();
    };
    document.addEventListener('selectionchange', remember);
    return () => document.removeEventListener('selectionchange', remember);
  }, []);

  useEffect(() => {
    const root = ref.current;
    if (!root || document.activeElement === root) return;
    if (serialize(root) === value) return;
    paint(root, value, labels);
  }, [value, labels]);

  const insert = (key: string, range?: Range | null) => {
    const root = ref.current;
    if (!root || disabled) return;
    root.focus();
    const saved = caret.current;
    let target = range && root.contains(range.startContainer)
      ? range
      : (saved && root.contains(saved.startContainer) ? saved : null);
    const dragging = root.querySelector('[data-dragging="1"]');
    if (target && dragging?.contains(target.startContainer) && dragging instanceof HTMLElement) {
      target = document.createRange();
      target.setStartAfter(dragging);
      target.collapse(true);
    }
    dragging?.remove();
    const chip = makeChip(key, labelOf(key));
    const sel = window.getSelection();
    if (target) {
      target.deleteContents();
      target.insertNode(chip);
      target.setStartAfter(chip);
      target.collapse(true);
      sel?.removeAllRanges();
      sel?.addRange(target);
      caret.current = target.cloneRange();
    } else {
      root.appendChild(chip);
    }
    onChange(serialize(root));
  };

  const known = (key: string) => Boolean(TOKEN_LABEL[key] || labels[key.toLowerCase()]);

  const saveForm = async () => {
    if (!form) return;
    setFormBusy(true);
    setFormError('');
    try {
      await onSaveBlock({ id: form.id || undefined, name: form.name, body: form.body });
      setForm(null);
    } catch (err: any) {
      setFormError(err?.response?.data?.error || 'Не удалось сохранить блок');
    } finally {
      setFormBusy(false);
    }
  };

  return (
    <>
      <div className="tz-block-head">
        <span>Из карточки</span>
      </div>
      <div className="tz-tray">
        {TOKENS.map((token) => (
          <button
            key={token.key}
            type="button"
            className="tz-chip"
            draggable={!disabled}
            disabled={disabled}
            title={token.hint}
            onMouseDown={(event) => event.preventDefault()}
            onDragStart={(event) => {
              event.dataTransfer.setData('text/tz-token', token.key);
              event.dataTransfer.setData('text/plain', token.key);
              event.dataTransfer.effectAllowed = 'copy';
            }}
            onClick={() => insert(token.key)}
          >
            {token.label}
          </button>
        ))}
      </div>
      {gather && (
        <>
          <div className="tz-block-head">
            <span>Со страницы</span>
          </div>
          <div className="tz-tray">
            <button
              type="button"
              className="tz-chip"
              draggable={!disabled}
              disabled={disabled}
              title="Ответ модели, которая смотрит страницу"
              onMouseDown={(event) => event.preventDefault()}
              onDragStart={(event) => {
                event.dataTransfer.setData('text/tz-token', 'сбор');
                event.dataTransfer.setData('text/plain', 'сбор');
                event.dataTransfer.effectAllowed = 'copy';
              }}
              onClick={() => insert('сбор')}
            >
              Сбор
            </button>
          </div>
        </>
      )}
      <div className="tz-block-head">
        <span>Блоки макета</span>
        <button
          type="button"
          className="tz-text"
          onClick={() => {
            setFormError('');
            setForm({ id: '', name: '', key: '', body: '' });
          }}
        >
          Добавить блок
        </button>
      </div>
      {blocks.length > 0 && (
        <div className="tz-blocks">
          {blocks.map((block) => (
            <div key={block.id} className="tz-block-wrap">
              <button
                type="button"
                className="tz-block"
                draggable={!disabled}
                disabled={disabled}
                title="Нажмите или перетащите в текст"
                onDragStart={(event) => {
                  event.dataTransfer.setData('text/tz-token', block.key);
                  event.dataTransfer.setData('text/plain', block.key);
                  event.dataTransfer.effectAllowed = 'copy';
                }}
                onClick={() => insert(block.key)}
              >
                <b>{block.name}</b>
                <small>{block.body}</small>
              </button>
              <button
                type="button"
                className="tz-block-edit"
                onClick={() => {
                  setFormError('');
                  setForm(block);
                }}
              >
                Изменить
              </button>
            </div>
          ))}
        </div>
      )}
      {blocks.length === 0 && !form && (
        <p className="tz-spend-empty">Пока нет блоков. Добавьте кусок, который повторяется в описаниях.</p>
      )}
      {form && (
        <div className="tz-block-form">
          <label>
            Имя
            <input
              value={form.name}
              autoFocus
              placeholder="Материалы"
              onChange={(event) => setForm({ ...form, name: event.target.value })}
            />
          </label>
          <p className="tz-muted">В тексте будет метка {`{{${blockKey(form.name) || '…'}}}`}</p>
          <label>
            Что искать
            <textarea
              value={form.body}
              rows={3}
              placeholder="Найди на странице материалы и перечисли их коротко"
              onChange={(event) => setForm({ ...form, body: event.target.value })}
            />
          </label>
          {formError && <p className="tz-alert">{formError}</p>}
          <div className="tz-actions">
            <button type="button" className="tz-primary" disabled={formBusy || !form.name.trim() || !form.body.trim()} onClick={saveForm}>
              {formBusy ? 'Сохраняю…' : (form.id ? 'Сохранить блок' : 'Добавить')}
            </button>
            <button type="button" className="tz-ghost" disabled={formBusy} onClick={() => setForm(null)}>Отмена</button>
            {form.id && (
              <button
                type="button"
                className="tz-ghost"
                disabled={formBusy}
                onClick={async () => {
                  setFormBusy(true);
                  setFormError('');
                  try {
                    await onDeleteBlock(form.id);
                    setForm(null);
                  } catch (err: any) {
                    setFormError(err?.response?.data?.error || 'Не удалось удалить блок');
                  } finally {
                    setFormBusy(false);
                  }
                }}
              >
                Удалить
              </button>
            )}
          </div>
        </div>
      )}
      <div
        ref={ref}
        className={`tz-prompt${value ? '' : ' is-empty'}`}
        contentEditable={!disabled}
        role="textbox"
        aria-multiline="true"
        data-placeholder="Соберите макет: слова пишите сами, метки перетащите. Модель заменит блок найденным значением."
        suppressContentEditableWarning
        onInput={(event) => onChange(serialize(event.currentTarget))}
        onDragOver={(event) => {
          if (disabled) return;
          event.preventDefault();
        }}
        onDrop={(event) => {
          event.preventDefault();
          const key = event.dataTransfer.getData('text/tz-token') || event.dataTransfer.getData('text/plain');
          if (!known(key)) return;
          insert(key, rangeFromPoint(event.clientX, event.clientY));
        }}
      />
    </>
  );
}

type SourceOption = { code: string; title: string };

type Props = {
  show: boolean;
  templates: TzTemplate[];
  bindings: TzBinding[];
  subjects: Subject[];
  sources?: SourceOption[];
  onHide: () => void;
  onChanged: () => Promise<void> | void;
  balance?: number | null;
};

function TemplateSpend({ item }: { item?: TzTemplate }) {
  const runs = item?.spend_runs || 0;
  if (!item) return null;
  if (!runs) {
    return <p className="tz-spend-empty">Расхода ещё нет. Он появится после заполнения карточки этим шаблоном.</p>;
  }
  return (
    <div className="tz-spend-strip">
      <div>
        <span>Среднее</span>
        <b>{money(item.spend_avg_cost)}</b>
        <small>{tokenCount(item.spend_avg_tokens)}</small>
      </div>
      <div>
        <span>Всего</span>
        <b>{money(item.spend_cost)}</b>
        <small>{tokenCount(item.spend_tokens)} · {runs} {runsWord(runs)}</small>
      </div>
    </div>
  );
}

function SpendLedger({
  report,
  loading,
  error,
  slice,
  onSlice,
  onOpen,
}: {
  report: SpendReport | null;
  loading: boolean;
  error: string;
  slice: (typeof SPEND_SLICES)[number]['id'];
  onSlice: (id: (typeof SPEND_SLICES)[number]['id']) => void;
  onOpen: (id: string) => void;
}) {
  const current = SPEND_SLICES.find((item) => item.id === slice) || SPEND_SLICES[0];
  const rows = report ? report[current.rows] : [];
  const totals = report?.totals;
  const max = rows.reduce((peak, row) => Math.max(peak, row.cost_rub ?? row.tokens), 0) || 1;
  return (
    <section className="tz-ledger">
      {error && <div className="tz-alert">{error}</div>}
      {loading && !report && <p className="tz-muted">Считаем расход…</p>}
      {totals && totals.runs === 0 && !loading && (
        <p className="tz-spend-empty">Пока пусто. Цифры появятся после первого заполнения карточки.</p>
      )}
      {totals && totals.runs > 0 && (
        <>
          <div className="tz-ledger-hero">
            <div>
              <b>{money(totals.cost_rub)}</b>
              <span>всего за {totals.runs} {runsWord(totals.runs)}</span>
            </div>
            <div className="tz-ledger-side">
              <div>
                <b>{money(totals.avg_cost)}</b>
                <span>среднее</span>
              </div>
              <div>
                <b>{tokenCount(totals.avg_tokens)}</b>
                <span>токенов в среднем</span>
              </div>
              <div>
                <b>{tokenCount(totals.tokens)}</b>
                <span>вход {tokenCount(totals.prompt_tokens)} · выход {tokenCount(totals.completion_tokens)}</span>
              </div>
            </div>
          </div>
          <div className="tz-slices" role="tablist">
            {SPEND_SLICES.map((item) => (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={item.id === slice}
                className={item.id === slice ? 'is-on' : undefined}
                onClick={() => onSlice(item.id)}
              >
                {item.title}
              </button>
            ))}
          </div>
          <div>
            {rows.map((row) => {
              const share = ((row.cost_rub ?? row.tokens) / max) * 100;
              const openable = slice === 'template' && row.key;
              const label = slice === 'model' ? modelLabel(row.label) : row.label;
              const body = (
                <>
                  <strong>{label}</strong>
                  <em>{row.cost_rub == null ? tokenCount(row.tokens) : money(row.cost_rub)}</em>
                  <span className="tz-ledger-meta">
                    {row.runs} {runsWord(row.runs)} · среднее {money(row.avg_cost)} · {tokenCount(row.avg_tokens)}
                    {' · '}вход {tokenCount(row.prompt_tokens)} · выход {tokenCount(row.completion_tokens)}
                  </span>
                  <span className="tz-share" aria-hidden="true"><i style={{ width: `${share}%` }} /></span>
                </>
              );
              if (!openable) {
                return <div key={row.key || row.label} className="tz-ledger-row">{body}</div>;
              }
              return (
                <button key={row.key} type="button" className="tz-ledger-row" onClick={() => onOpen(row.key)}>
                  {body}
                </button>
              );
            })}
          </div>
        </>
      )}
    </section>
  );
}

function ModelPicker({
  value,
  options,
  loading,
  disabled,
  onChange,
}: {
  value: string;
  options: TzModel[];
  loading: boolean;
  disabled: boolean;
  onChange: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const box = useCloseOutside(open, close);
  const picked = options.find((item) => item.id === value);
  return (
    <div className="tz-model" ref={box}>
      <button
        type="button"
        className="tz-model-button"
        disabled={disabled || loading}
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <span className="tz-model-name">
          <span>{loading ? 'Загружаю модели…' : (value ? modelLabel(value) : 'Выберите модель')}</span>
          {!loading && (picked?.vision || modelSees(value)) && <i className="tz-vision" title="Видит фото">зрение</i>}
        </span>
        {picked && picked.input != null && (
          <span className="tz-model-rate">
            {rub(picked.input)}
            <span aria-hidden>·</span>
            <b>{rub(picked.output)}</b>
            <span className="tz-model-unit">руб/1м т.</span>
          </span>
        )}
      </button>
      {open && (
        <ul className="tz-model-list" onMouseDown={(event) => event.preventDefault()}>
          <li className="tz-model-head">
            <span>Модель</span>
            <span>вход</span>
            <span>выход</span>
            <span />
          </li>
          {options.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                className={item.id === value ? 'is-on' : undefined}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  onChange(item.id);
                  setOpen(false);
                }}
              >
                <ModelName id={item.id} missing={item.missing} vision={item.vision} />
                <span>{item.missing ? '' : rub(item.input)}</span>
                <span>{item.missing ? '' : rub(item.output)}</span>
                {!item.missing && <span className="tz-model-unit">руб/1м т.</span>}
              </button>
            </li>
          ))}
          {options.length === 0 && <li className="tz-muted" style={{ padding: 8 }}>Список пуст</li>}
        </ul>
      )}
    </div>
  );
}

const TzTemplatesModal: React.FC<Props> = ({ show, templates, bindings, subjects, sources = [], onHide, onChanged, balance }) => {
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const browserPromptRef = useRef<HTMLTextAreaElement>(null);
  const [models, setModels] = useState<TzModel[]>([]);
  const [modelsError, setModelsError] = useState('');
  const [modelsLoading, setModelsLoading] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState<'edit' | 'defaults' | 'spend'>('edit');
  const [spend, setSpend] = useState<SpendReport | null>(null);
  const [spendError, setSpendError] = useState('');
  const [spendLoading, setSpendLoading] = useState(false);
  const [spendSlice, setSpendSlice] = useState<(typeof SPEND_SLICES)[number]['id']>('template');
  const [blocks, setBlocks] = useState<PromptBlock[]>([]);
  const [subjectId, setSubjectId] = useState('');
  const [sourceCode, setSourceCode] = useState('');
  const [useDefault, setUseDefault] = useState(false);
  const [nicheQuery, setNicheQuery] = useState('');
  const [nicheOpen, setNicheOpen] = useState(false);
  const [placeOpen, setPlaceOpen] = useState(false);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [stars, setStars] = useState(0);
  const [rateSubjectId, setRateSubjectId] = useState('');
  const [rateQuery, setRateQuery] = useState('');
  const [rateOpen, setRateOpen] = useState(false);
  const [commentText, setCommentText] = useState('');

  const loadModels = useCallback(() => {
    setModelsLoading(true);
    setModelsError('');
    api.getTzModels()
      .then((response) => setModels(asModels(response.data?.data)))
      .catch((err) => setModelsError(err.response?.data?.error || 'Список моделей не загрузился'))
      .finally(() => setModelsLoading(false));
  }, []);

  useEffect(() => {
    if (!show) return;
    setError('');
    loadModels();
    onChanged();
    api.getTzBlocks().then((response) => setBlocks(response.data?.data || [])).catch(() => {});
  }, [show, loadModels, onChanged]);

  useEffect(() => {
    if (!show || tab !== 'spend') return undefined;
    let cancel = false;
    setSpendLoading(true);
    setSpendError('');
    api.getTzUsage()
      .then((response) => {
        if (!cancel) setSpend(response.data?.data || null);
      })
      .catch((err) => {
        if (!cancel) setSpendError(err.response?.data?.error || 'Расход не загрузился');
      })
      .finally(() => {
        if (!cancel) setSpendLoading(false);
      });
    return () => {
      cancel = true;
    };
  }, [show, tab]);

  const modelOptions = useMemo(() => {
    const list = models.map((item) => ({ ...item }));
    [draft.model_id, draft.browser_model_id].forEach((id) => {
      if (id && !list.some((item) => item.id === id)) {
        list.unshift({ id, input: null, output: null, missing: true });
      }
    });
    return list.sort((a, b) => {
      if (a.missing !== b.missing) return a.missing ? -1 : 1;
      if (a.input == null && b.input != null) return 1;
      if (a.input != null && b.input == null) return -1;
      if (a.input != null && b.input != null && a.input !== b.input) return a.input - b.input;
      return modelLabel(a.id).localeCompare(modelLabel(b.id), 'ru');
    });
  }, [models, draft.model_id, draft.browser_model_id]);

  const nicheMatches = useMemo(() => {
    const query = nicheQuery.trim().toLowerCase();
    const source = query
      ? subjects.filter((subject) => formatSubjectName(subject.name).toLowerCase().includes(query))
      : subjects;
    return { items: source.slice(0, 80), more: source.length > 80 };
  }, [subjects, nicheQuery]);

  const selectedNiche = subjects.find((subject) => String(subject.id) === subjectId);
  const nicheLabel = (id: number | null) => {
    const subject = subjects.find((item) => String(item.id) === String(id));
    return subject ? formatSubjectName(subject.name) : 'Ниша';
  };
  const sourceTitle = (code?: string | null) => sources.find((item) => item.code === code)?.title || code || '';
  const placeHead = (item: TzBinding) => {
    if (item.scope === 'global' || item.scope === 'user') return 'Все товары';
    const placeName = sourceTitle(item.source_code);
    if (item.scope === 'source' || item.scope === 'user_source') return placeName || 'Площадка';
    return `${nicheLabel(item.subject_id)}${placeName ? ` · ${placeName}` : ''}`;
  };
  const placeBindings = bindings.slice().sort((a, b) => placeHead(a).localeCompare(placeHead(b), 'ru') || (a.template_id || '').localeCompare(b.template_id || ''));

  const readOnly = !!draft.id && !draft.is_owner;
  const savedPublic = !!draft.id && templates.some((item) => item.id === draft.id && item.is_public);
  const showFeedback = !!draft.id && (savedPublic || draft.is_owner);
  const rateMatches = useMemo(() => {
    const query = rateQuery.trim().toLowerCase();
    const source = query
      ? subjects.filter((subject) => formatSubjectName(subject.name).toLowerCase().includes(query))
      : subjects;
    return { items: source.slice(0, 80), more: source.length > 80 };
  }, [subjects, rateQuery]);
  const ratedNiche = subjects.find((subject) => String(subject.id) === rateSubjectId);
  const closeNiche = useCallback(() => setNicheOpen(false), []);
  const closePlace = useCallback(() => setPlaceOpen(false), []);
  const closeRate = useCallback(() => setRateOpen(false), []);
  const nicheBox = useCloseOutside(nicheOpen, closeNiche);
  const placeBox = useCloseOutside(placeOpen, closePlace);
  const rateBox = useCloseOutside(rateOpen, closeRate);

  useEffect(() => {
    if (!show || !draft.id || !(savedPublic || draft.is_owner)) {
      setFeedback(null);
      return undefined;
    }
    let cancel = false;
    api.getTzFeedback(draft.id).then((response) => {
      if (!cancel) setFeedback(response.data?.data || null);
    }).catch(() => {
      if (!cancel) setFeedback(null);
    });
    return () => { cancel = true; };
  }, [show, draft.id, savedPublic, draft.is_owner]);

  useEffect(() => {
    const row = feedback?.mine.find((item) => String(item.subject_id ?? '') === rateSubjectId);
    setStars(row?.score || 0);
  }, [feedback, rateSubjectId]);

  const bindingId = (scope: TzBinding['scope'], nicheId?: string, place?: string) => {
    const want = place || '';
    const row = bindings.find((item) => {
      if (item.scope !== scope || (item.source_code || '') !== want) return false;
      if (scope === 'niche' || scope === 'user_niche') return String(item.subject_id) === nicheId;
      return true;
    });
    return row?.template_id || '';
  };
  const targetScope = scopeOf(savedPublic, subjectId, sourceCode);
  const occupant = draft.id ? bindingId(targetScope, subjectId, sourceCode) : '';
  const displace = occupant && occupant !== draft.id
    ? templates.find((item) => item.id === occupant)?.name || ''
    : '';

  const save = async () => {
    setBusy(true);
    setError('');
    const body = {
      name: draft.name,
      prompt: draft.prompt,
      model_id: draft.model_id,
      fill_desc: draft.fill_desc,
      fill_eng_desc: draft.fill_eng_desc,
      fill_season: draft.fill_season,
      is_public: draft.is_public,
      browser_model_id: draft.browser_on ? draft.browser_model_id : '',
      browser_prompt: draft.browser_on ? draft.browser_prompt : '',
      model_search: draft.model_search,
    };
    try {
      const creating = !draft.id;
      const response = draft.id
        ? await api.updateTzTemplate(draft.id, body)
        : await api.createTzTemplate(body);
      const saved = response.data?.data;
      if (creating && saved?.id && useDefault) {
        const scope = scopeOf(!!saved.is_public, subjectId, sourceCode);
        await api.setTzBinding({
          scope,
          template_id: saved.id,
          subject_id: scope === 'niche' || scope === 'user_niche' ? Number(subjectId) : undefined,
          source_code: scope === 'global' || scope === 'user' ? null : (sourceCode || null),
        });
      }
      await onChanged();
      if (saved?.id) setDraft(draftFrom(saved));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Не удалось сохранить шаблон');
    } finally {
      setBusy(false);
    }
  };

  const act = async (work: () => Promise<any>) => {
    setBusy(true);
    setError('');
    try {
      await work();
      await onChanged();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Не удалось сохранить');
    } finally {
      setBusy(false);
    }
  };

  const clearBinding = (item: TzBinding) => api.clearTzBinding(
    item.scope,
    item.subject_id == null ? undefined : Number(item.subject_id),
    item.source_code || undefined,
  );

  const adopt = (item: TzTemplate | null) => {
    setNicheOpen(false);
    setPlaceOpen(false);
    if (!item) {
      setDraft(emptyDraft());
      setSubjectId('');
      setSourceCode('');
      setUseDefault(false);
      return;
    }
    setDraft(draftFrom(item));
    const mine = bindings.filter((row) => row.template_id === item.id);
    const row = mine[0];
    setSubjectId(row?.subject_id == null ? '' : String(row.subject_id));
    setSourceCode(row?.source_code || '');
    setUseDefault(mine.length > 0);
  };

  const commitDefault = (on: boolean, subject: string, source: string) => {
    setUseDefault(on);
    setSubjectId(subject);
    setSourceCode(source);
    if (!draft.id) return undefined;
    const id = draft.id;
    const isPublic = !!templates.find((item) => item.id === id)?.is_public;
    return act(async () => {
      const mine = bindings.filter((row) => row.template_id === id);
      for (const item of mine) await clearBinding(item);
      if (!on) return;
      const scope = scopeOf(isPublic, subject, source);
      await api.setTzBinding({
        scope,
        template_id: id,
        subject_id: scope === 'niche' || scope === 'user_niche' ? Number(subject) : undefined,
        source_code: scope === 'global' || scope === 'user' ? null : (source || null),
      });
    });
  };

  const pickNiche = (id: string) => {
    setNicheQuery('');
    setNicheOpen(false);
    if (useDefault && draft.id) commitDefault(true, id, sourceCode);
    else setSubjectId(id);
  };

  const pickPlace = (code: string) => {
    setPlaceOpen(false);
    if (useDefault && draft.id) commitDefault(true, subjectId, code);
    else setSourceCode(code);
  };

  const toggleField = (key: 'fill_desc' | 'fill_eng_desc' | 'fill_season') => {
    setDraft({ ...draft, [key]: !draft[key] });
  };

  return (
    <Modal show={show} onHide={onHide} dialogClassName="tz-studio-dialog" contentClassName="tz-studio" centered>
      <Modal.Header closeButton closeVariant="white">
        <Modal.Title>
          <div className="tz-title-row">
            <div>
              Шаблоны
              <span className="tz-kicker">Текст, по которому заполняется карточка товара</span>
            </div>
            {balance != null && (
              <div className="tz-head-balance" title="Списано за заполнение карточек">
                Баланс
                <b>{money(balance)}</b>
              </div>
            )}
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="tz-tabs">
          <button type="button" className={`tz-tab${tab === 'edit' ? ' is-on' : ''}`} onClick={() => setTab('edit')}>Шаблон</button>
          <button type="button" className={`tz-tab${tab === 'defaults' ? ' is-on' : ''}`} onClick={() => setTab('defaults')}>По умолчанию</button>
          <button type="button" className={`tz-tab${tab === 'spend' ? ' is-on' : ''}`} onClick={() => setTab('spend')}>Расход</button>
        </div>
        {error && <div className="tz-alert">{error}</div>}
        {tab === 'edit' && <div className="tz-layout">
          <aside className="tz-rail">
            <button type="button" className="tz-new" onClick={() => adopt(null)}>Новый шаблон</button>
            {templates.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`tz-item${item.id === draft.id ? ' is-on' : ''}`}
                onClick={() => adopt(item)}
              >
                {item.name}
                <small>
                  {item.is_owner ? (item.is_public ? 'виден коллегам' : 'только вам') : 'шаблон коллеги'}
                  {item.is_public && (item.score_count || 0) > 0 ? ` · ${item.score_avg}` : ''}
                </small>
                {(item.spend_runs || 0) > 0 && (
                  <small className="tz-cost">{money(item.spend_avg_cost)} · {tokenCount(item.spend_avg_tokens)} среднее</small>
                )}
              </button>
            ))}
            {templates.length === 0 && <div className="tz-muted">Пока нет ни одного шаблона</div>}
          </aside>

          <div className="tz-main">
            {templates.length === 0 && !draft.id && (
              <p className="tz-muted" style={{ marginTop: 0 }}>
                Название, модель и текст. Ниша и площадка — куда шаблон подставляется сам.
              </p>
            )}
            {draft.id && <TemplateSpend item={templates.find((item) => item.id === draft.id)} />}
            {readOnly && <div className="tz-note">Это шаблон коллеги. Чтобы изменить текст, сделайте копию.</div>}
            <div className="tz-row">
              <label className="tz-field">
                Название
                <input
                  value={draft.name}
                  disabled={readOnly}
                  placeholder="Например, одежда на зиму"
                  onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                />
              </label>
              <div className="tz-field">
                Модель карточки
                <ModelPicker
                  value={draft.model_id}
                  options={modelOptions}
                  loading={modelsLoading}
                  disabled={readOnly}
                  onChange={(id) => setDraft({ ...draft, model_id: id })}
                />
                {modelsError && (
                  <span className="tz-muted">
                    {modelsError}. <button type="button" className="tz-ghost" onClick={loadModels}>Повторить</button>
                  </span>
                )}
              </div>
            </div>

            <div className="tz-default">
              <button
                type="button"
                className={`tz-switch${draft.model_search ? ' is-on' : ''}`}
                aria-pressed={draft.model_search}
                disabled={readOnly}
                onClick={() => setDraft({ ...draft, model_search: !draft.model_search })}
              >
                <i />
              </button>
              <span>
                Поиск модели
                <small className="tz-muted">Каждый переход стоит 0.8 ₽</small>
              </span>
            </div>

            <div className="tz-gather">
              <div className="tz-gather-head">
                <button
                  type="button"
                  className={`tz-switch${draft.browser_on ? ' is-on' : ''}`}
                  aria-pressed={draft.browser_on}
                  disabled={readOnly}
                  onClick={() => setDraft({ ...draft, browser_on: !draft.browser_on })}
                >
                  <i />
                </button>
                <span>
                  Смотреть страницу отдельной моделью
                  <small className="tz-muted">
                    {draft.browser_on
                      ? 'Она собирает факты. Модель карточки пишет по макету и страницу уже не открывает.'
                      : 'Выключено: модель карточки сама открывает ссылку, если макет это просит.'}
                  </small>
                </span>
              </div>
              {!draft.browser_on && /\{\{\s*сбор\s*\}\}/i.test(draft.prompt) && (
                <p className="tz-muted tz-replace">В макете стоит «Сбор». Включите модель или уберите метку.</p>
              )}
              {draft.browser_on && (
                <div className="tz-gather-body">
                  <label className="tz-field">
                    Модель страницы
                    <ModelPicker
                      value={draft.browser_model_id}
                      options={modelOptions}
                      loading={modelsLoading}
                      disabled={readOnly}
                      onChange={(id) => setDraft({ ...draft, browser_model_id: id })}
                    />
                  </label>
                  <label className="tz-field">
                    Задание
                    <div className="tz-tray">
                      {TOKENS.map((token) => (
                        <button
                          key={token.key}
                          type="button"
                          className="tz-chip"
                          disabled={readOnly}
                          title={token.hint}
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => {
                            const tokenText = `{{${token.key}}}`;
                            const current = draft.browser_prompt || '';
                            const node = browserPromptRef.current;
                            const start = node ? node.selectionStart : current.length;
                            const end = node ? node.selectionEnd : current.length;
                            const next = current.slice(0, start) + tokenText + current.slice(end);
                            setDraft({ ...draft, browser_prompt: next });
                            const pos = start + tokenText.length;
                            requestAnimationFrame(() => {
                              const area = browserPromptRef.current;
                              if (!area) return;
                              area.focus();
                              area.setSelectionRange(pos, pos);
                            });
                          }}
                        >
                          {token.label}
                        </button>
                      ))}
                    </div>
                    <textarea
                      ref={browserPromptRef}
                      value={draft.browser_prompt}
                      disabled={readOnly}
                      maxLength={8000}
                      placeholder="Открой {{url}}. Если товара нет, найди карточку по названию {{name}}."
                      onChange={(event) => setDraft({ ...draft, browser_prompt: event.target.value })}
                    />
                    <span className="tz-muted">Метка подставится до запуска. «Ссылка» — адрес карточки, с него модель и начинает. Ответ встанет в макет вместо «Сбор».</span>
                  </label>
                  {!/\{\{\s*сбор\s*\}\}/i.test(draft.prompt) && (
                    <p className="tz-muted tz-replace">Метки «Сбор» в макете нет — отдельная модель не запустится.</p>
                  )}
                </div>
              )}
            </div>

            <div className="tz-row">
              <div className="tz-field">
                Что записать в карточку
                <div className="tz-pills">
                  <button type="button" className={`tz-pill${draft.fill_desc ? ' is-on' : ''}`} disabled={readOnly} onClick={() => toggleField('fill_desc')}>Описание</button>
                  <button type="button" className={`tz-pill${draft.fill_eng_desc ? ' is-on' : ''}`} disabled={readOnly} onClick={() => toggleField('fill_eng_desc')}>Описание на английском</button>
                  <button type="button" className={`tz-pill${draft.fill_season ? ' is-on' : ''}`} disabled={readOnly} onClick={() => toggleField('fill_season')}>Сезон</button>
                </div>
              </div>
              <div className="tz-field">
                Кто видит шаблон
                <div className="tz-pills">
                  <button type="button" className={`tz-pill${!draft.is_public ? ' is-on' : ''}`} disabled={readOnly} onClick={() => setDraft({ ...draft, is_public: false })}>Только я</button>
                  <button type="button" className={`tz-pill${draft.is_public ? ' is-on' : ''}`} disabled={readOnly} onClick={() => setDraft({ ...draft, is_public: true })}>Коллеги тоже</button>
                </div>
              </div>
            </div>

            <div className="tz-row">
              <div className="tz-field">
                Ниша
                <div className="tz-pick" ref={nicheBox}>
                  <button
                    type="button"
                    className="tz-model-button"
                    disabled={readOnly}
                    aria-expanded={nicheOpen}
                    onClick={() => setNicheOpen((open) => !open)}
                  >
                    <span>{selectedNiche ? formatSubjectName(selectedNiche.name) : 'Любая'}</span>
                    <span className="tz-caret" aria-hidden>▾</span>
                  </button>
                  {nicheOpen && (
                    <ul className="tz-niche-list" onMouseDown={(event) => event.preventDefault()}>
                      <li>
                        <input
                          className="tz-search"
                          value={nicheQuery}
                          placeholder="Найти нишу"
                          autoFocus
                          onMouseDown={(event) => event.stopPropagation()}
                          onChange={(event) => setNicheQuery(event.target.value)}
                        />
                      </li>
                      <li>
                        <button type="button" className={!selectedNiche ? 'is-on' : undefined} onMouseDown={(event) => event.preventDefault()} onClick={() => pickNiche('')}>
                          Любая
                        </button>
                      </li>
                      {nicheMatches.items.map((subject) => (
                        <li key={subject.id}>
                          <button
                            type="button"
                            className={String(subject.id) === subjectId ? 'is-on' : undefined}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => pickNiche(String(subject.id))}
                          >
                            {formatSubjectName(subject.name)}
                          </button>
                        </li>
                      ))}
                      {subjects.length === 0 && <li className="tz-muted" style={{ padding: 8 }}>Ниши ещё не загрузились</li>}
                      {subjects.length > 0 && nicheMatches.items.length === 0 && <li className="tz-muted" style={{ padding: 8 }}>Нет такой ниши</li>}
                      {nicheMatches.more && <li className="tz-muted" style={{ padding: 8 }}>Показаны первые 80. Уточните поиск.</li>}
                    </ul>
                  )}
                </div>
              </div>
              <div className="tz-field">
                Площадка
                <div className="tz-pick" ref={placeBox}>
                  <button
                    type="button"
                    className="tz-model-button"
                    disabled={readOnly}
                    aria-expanded={placeOpen}
                    onClick={() => setPlaceOpen((open) => !open)}
                  >
                    <span>{sourceCode ? sourceTitle(sourceCode) : 'Любая'}</span>
                    <span className="tz-caret" aria-hidden>▾</span>
                  </button>
                  {placeOpen && (
                    <ul className="tz-niche-list" onMouseDown={(event) => event.preventDefault()}>
                      <li>
                        <button type="button" className={!sourceCode ? 'is-on' : undefined} onMouseDown={(event) => event.preventDefault()} onClick={() => pickPlace('')}>
                          Любая
                        </button>
                      </li>
                      {sources.map((source) => (
                        <li key={source.code}>
                          <button
                            type="button"
                            className={sourceCode === source.code ? 'is-on' : undefined}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => pickPlace(source.code)}
                          >
                            {source.title}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
            <div className="tz-default">
              <button
                type="button"
                className={`tz-switch${useDefault ? ' is-on' : ''}`}
                aria-pressed={useDefault}
                disabled={readOnly || busy}
                onClick={() => commitDefault(!useDefault, subjectId, sourceCode)}
              >
                <i />
              </button>
              <span>
                Подставлять по умолчанию
                <small className="tz-muted">
                  {draft.is_public
                    ? 'Общий шаблон подставляется всем, пока в строке не выбран другой.'
                    : 'Личный шаблон подставляется только вам.'}
                  {' '}Выключите, если он не должен конкурировать.
                </small>
              </span>
            </div>
            {displace && useDefault && (
              <p className="tz-muted tz-replace">Сейчас на этом месте «{displace}». Смена заменит его.</p>
            )}
            {!draft.id && useDefault && (
              <p className="tz-muted tz-replace">Заработает после сохранения шаблона.</p>
            )}

            <div className="tz-field">
              Макет описания
              <span className="tz-muted">Слова вокруг меток останутся в тексте. Блок модель заполнит по своей инструкции, данные карточки подставятся сами.</span>
              <PromptEditor
                value={draft.prompt}
                disabled={readOnly}
                blocks={blocks}
                gather={draft.browser_on}
                onChange={(prompt) => setDraft((prev) => ({ ...prev, prompt }))}
                onSaveBlock={async (block) => {
                  if (block.id) await api.updateTzBlock(block.id, { name: block.name, body: block.body });
                  else await api.createTzBlock({ name: block.name, body: block.body });
                  const response = await api.getTzBlocks();
                  setBlocks(response.data?.data || []);
                }}
                onDeleteBlock={async (id) => {
                  await api.deleteTzBlock(id);
                  setBlocks((prev) => prev.filter((item) => item.id !== id));
                }}
              />
            </div>

            <div className="tz-actions">
              <button type="button" className="tz-primary" disabled={busy || readOnly} onClick={save}>
                {busy ? 'Сохраняю…' : 'Сохранить шаблон'}
              </button>
              {draft.id && (
                <button
                  type="button"
                  className="tz-ghost"
                  disabled={busy}
                  onClick={() => act(async () => {
                    const response = await api.duplicateTzTemplate(draft.id as string);
                    const saved = response.data?.data;
                    if (saved?.id) setDraft(draftFrom(saved));
                  })}
                >
                  Сделать копию
                </button>
              )}
              {draft.id && draft.is_owner && (
                <button
                  type="button"
                  className="tz-danger"
                  disabled={busy}
                  onClick={() => act(async () => {
                    await api.deleteTzTemplate(draft.id as string);
                    setDraft(emptyDraft());
                  })}
                >
                  Удалить
                </button>
              )}
            </div>

            {showFeedback && (
              <div className="tz-field tz-feedback">
                <span>
                  Оценка
                  <strong>{feedback && feedback.score_count > 0 ? ` ${feedback.score_avg} из 5 · ${feedback.score_count}` : ' пока нет'}</strong>
                </span>
                <>
                    <div className="tz-pills">
                      {[1, 2, 3, 4, 5].map((value) => (
                        <button
                          key={value}
                          type="button"
                          className={`tz-pill${stars >= value ? ' is-on' : ''}`}
                          disabled={busy}
                          onClick={() => setStars(value)}
                        >
                          {value}
                        </button>
                      ))}
                    </div>
                    {ratedNiche ? (
                      <button type="button" className="tz-niche-current" onClick={() => { setRateSubjectId(''); setRateQuery(''); }}>
                        {formatSubjectName(ratedNiche.name)}
                        <span aria-hidden>×</span>
                      </button>
                    ) : (
                      <div ref={rateBox}>
                        <input
                          className="tz-search"
                          value={rateQuery}
                          placeholder="Ниша, если оцениваете применение. Пусто — общая оценка"
                          onClick={() => setRateOpen(true)}
                          onFocus={() => setRateOpen(true)}
                          onChange={(event) => {
                            setRateQuery(event.target.value);
                            setRateOpen(true);
                          }}
                        />
                        {rateOpen && (
                          <ul className="tz-niche-list" onMouseDown={(event) => event.preventDefault()}>
                            {rateMatches.items.map((subject) => (
                              <li key={subject.id}>
                                <button
                                  type="button"
                                  onMouseDown={(event) => event.preventDefault()}
                                  onClick={() => {
                                    setRateSubjectId(String(subject.id));
                                    setRateQuery('');
                                    setRateOpen(false);
                                  }}
                                >
                                  {formatSubjectName(subject.name)}
                                </button>
                              </li>
                            ))}
                            {rateMatches.more && <li className="tz-muted" style={{ padding: 8 }}>Показаны первые 80. Уточните поиск.</li>}
                          </ul>
                        )}
                      </div>
                    )}
                    <button
                      type="button"
                      className="tz-primary"
                      disabled={busy || stars < 1}
                      onClick={() => act(async () => {
                        await api.rateTzTemplate(draft.id as string, {
                          score: stars,
                          subject_id: rateSubjectId ? Number(rateSubjectId) : null,
                        });
                        const response = await api.getTzFeedback(draft.id as string);
                        setFeedback(response.data?.data || null);
                      })}
                    >
                      Поставить оценку
                    </button>
                </>
                {feedback && feedback.mine.length > 0 && (
                  <span className="tz-muted">
                    Ваши оценки: {feedback.mine.map((item) => (
                      item.subject_id == null ? `общая ${item.score}` : `${nicheLabel(item.subject_id)} ${item.score}`
                    )).join(', ')}
                  </span>
                )}
                <div className="tz-comments">
                  {(feedback?.comments || []).map((item) => (
                    <div key={item.id} className="tz-comment">
                      <span>{item.author} · {when(item.created_at)}</span>
                      <p>{item.body}</p>
                      {item.mine && (
                        <button
                          type="button"
                          className="tz-ghost"
                          disabled={busy}
                          onClick={() => act(async () => {
                            await api.deleteTzComment(draft.id as string, item.id);
                            const response = await api.getTzFeedback(draft.id as string);
                            setFeedback(response.data?.data || null);
                          })}
                        >
                          Удалить
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <textarea
                  className="tz-comment-input"
                  value={commentText}
                  placeholder="Комментарий к шаблону"
                  onChange={(event) => setCommentText(event.target.value)}
                />
                <button
                  type="button"
                  className="tz-ghost"
                  disabled={busy || !commentText.trim()}
                  onClick={() => act(async () => {
                    await api.commentTzTemplate(draft.id as string, commentText.trim());
                    setCommentText('');
                    const response = await api.getTzFeedback(draft.id as string);
                    setFeedback(response.data?.data || null);
                  })}
                >
                  Отправить комментарий
                </button>
              </div>
            )}
          </div>
        </div>}

        {tab === 'spend' && (
          <SpendLedger
            report={spend}
            loading={spendLoading}
            error={spendError}
            slice={spendSlice}
            onSlice={setSpendSlice}
            onOpen={(id) => {
              const found = templates.find((item) => item.id === id);
              if (!found) return;
              adopt(found);
              setTab('edit');
            }}
          />
        )}

        {tab === 'defaults' && (
        <section className="tz-rules is-page">
          <h3>Что подставляется само</h3>
          <p className="tz-muted">Ниша и площадка задаются в шаблоне. Общий шаблон действует на всех, пока в строке товара не выбран другой. Выключите, чтобы он не конкурировал.</p>
          {placeBindings.length > 0 ? (
            <ul className="tz-rule-list">
              {placeBindings.map((item) => {
                const template = templates.find((row) => row.id === item.template_id);
                return (
                  <li key={`${item.scope}-${item.subject_id || ''}-${item.source_code || ''}-${item.template_id}`} className="tz-rule">
                    <b>{placeHead(item)}</b>
                    <button
                      type="button"
                      className="tz-link"
                      onClick={() => {
                        if (template) adopt(template);
                        setTab('edit');
                      }}
                    >
                      {template?.name || 'Шаблон'}
                    </button>
                    <button
                      type="button"
                      className="tz-switch is-on"
                      aria-pressed
                      disabled={busy}
                      aria-label="Выключить"
                      onClick={() => act(async () => {
                        await clearBinding(item);
                        const left = bindings.filter((row) => row.template_id === item.template_id && row !== item);
                        if (item.template_id === draft.id && left.length === 0) setUseDefault(false);
                      })}
                    >
                      <i />
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="tz-muted tz-replace">Пока ни один шаблон не подставляется сам. Включите это в шаблоне.</p>
          )}
        </section>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default TzTemplatesModal;
